import { AnalyticsBrowser } from '@segment/analytics-next'
import { SEGMENT_KEY } from "@/config-global.jsx";
import { handleError } from "@/services/errorHandler.js";
import posthog from 'posthog-js'
import { AnalyticsBrowser as CustomerIOAnalyticsBrowser } from '@customerio/cdp-analytics-browser'

let analytics
let customerIOAnalytics

// The current account group id (set by AnalyticsService.group). Segment carries no
// group state between calls, so we stamp $groups on every track() from this — the
// mirror of the backend analytics-client injection, so persons link to the account
// group in PostHog.
let currentGroupId = null

// Why do I need this? if it's always used one time its not repetitive,
export const EVENT_NAMES = {
    CONTENT_GENERATED: 'content-generated',
    CONTENT_MANUALLY_CHANGED: 'content-manually-changed',
    CONTENT_COPIED: 'content-copied',
    HTML_DOWNLOADED: 'html-downloaded',
    CONTENT_ADJUSTED: 'content-adjusted',
    CONTENT_REGENERATED: 'content-regenerated',
    NAVIGATED_TO_STUDIO_FROM_LIBRARY: 'navigated-to-studio-from-library',
    CONTENT_CARD_CLICKED: 'content-card-clicked',
    CHOSEN_PRODUCT_CHANGED: 'chosen-product-changed',
    APPLY_CONTENT_TO_MARKETING_PLAN: 'apply-content-to-marketing-plan',
    MARKETING_CONTENT_REPLACED: 'marketing-content-replaced',
    NAVIGATED_TO_STUDIO_FROM_CALENDAR: 'navigated-to-studio-from-calendar',
    SCHEDULED_MATERIAL_DELETED: 'scheduled-material-deleted',
    CREATE_CONTENT_FROM_NAVBAR: 'create-content-from-navbar',
    CREATE_CONTENT_FROM_CALENDAR: 'create-content-from-calendar',
    CREATION_FLOW_CHANGED_EMAIL_TYPE: 'creation-flow-changed-email-type',
    CREATION_FLOW_CHANGED_USE_CASE: 'creation-flow-changed-use-case',
    CREATION_FLOW_CHANGED_CUSTOM_PROMPT_CHANGED: 'creation-flow-changed-custom-prompt-changed',
    INTEGRATION_CONNECTION_STARTED: 'integration-connection-started',
    INTEGRATION_CONNECTION_ENDED: 'integration-connection-ended',
    INTEGRATION_CONNECTION_FAILED: 'integration-connection-failed',
    INTEGRATION_COMING_SOON_CLICKED: 'integration-coming-soon-clicked',
    MARKETING_MATERIAL_DELETED: 'marketing-material-deleted',
    LIBRARY_CHOSEN_TOPIC_CHANGED: 'library-chosen-topic-changed',
    CONTENT_SCHEDULED_ON_CALENDAR: 'content-scheduled-on-calendar',
    CONTENT_REMOVED_FROM_CALENDAR: 'content-removed-from-calendar',
    CONTENT_SCHEDULE_CHANGED_ON_CALENDAR: 'content-schedule-changed-on-calendar',
    MARKETING_MATERIAL_TEMPLATE_UPDATED: 'marketing-material-template-updated',
    UPDATE_SCHEDULE_MATERIAL_AFTER_CAMPAIGN_SCHEDULE_CHANGE: 'update-schedule-material-after-campaign-schedule-change',
    CREATE_SCHEDULE_MATERIAL_AFTER_CAMPAIGN_CREATED: 'create-schedule-material-after-campaign-created',
    INJECT_INITIAL_EMAIL_TEMPLATE: 'inject-initial-email-template',
    TEMPLATE_DEPLOYED_TO_KLAVIYO: 'template-deployed-to-klaviyo',
    TEMPLATE_ONLY_DEPLOYED_TO_KLAVIYO: 'template-only-deployed-to-klaviyo',
    CAMPAIGN_DEPLOYED_TO_KLAVIYO: 'campaign-deployed-to-klaviyo',
    CAMPAIGN_DELETED: 'campaign-deleted',
    COUPON_MENTIONED: 'coupon-mentioned',
    TEXT_ADJUSTED: 'text-adjusted',
    IMAGE_CHOSEN: 'image-chosen',
    PRODUCT_SEARCHED_ON_EMAIL_STUDIO_IMAGE_DIALOG: 'product-searched-on-email-studio-image-dialog',
    PRODUCT_SEARCHED_ON_MATERIAL_CREATING_FLOW: 'product-searched-on-material-creating-flow',
    CREATION_DRAWER_OPENED: 'creation-drawer-opened',
    CREATION_DRAWER_CLOSED: 'creation-drawer-closed',
    INITIALIZE_START: 'initialize-start',
    INITIALIZE_CHECK: 'initialize-check',
    INITIALIZE_END: 'initialize-end',
    LEAVE_STUDIO: 'leave-studio',
    ACCEPT_CONTENT_SUGGESTION_FOR_A_STORE_EVENT: 'accept-content-suggestion-for-a-store-event',
    DISMISS_NOTIFICATION: 'dismiss-notification',
    LOGOUT: 'logout',
    PRIVACY_POLICY_CLICKED: 'privacy-policy-clicked',
    MCP_DISCOVERY_SHOWN: 'mcp-discovery-shown',
    MCP_DISCOVERY_ANSWER_CHANGED: 'mcp-discovery-answer-changed',
    MCP_DISCOVERY_SUBMITTED: 'mcp-discovery-submitted',
}

/* This is the Adapter layer to the 3rd party analytics library we gonna use, currently we will use segment to collect the event and stream them into Mixpanel for the client. */
export const AnalyticsService = {
    init() {
        try {
            console.log('Analytics init')
            analytics = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY })
            // customerIOAnalytics = CustomerIOAnalyticsBrowser.load({ writeKey: 'cb1adab1efb4bdcc279f' })
        } catch (e) {
            console.log(e)
            handleError(e)
        }

    },
    identify(userId, data = {}) {
        try {
            // Guard against carrying a previous user's identity into a new one.
            // Some logout paths (e.g. the 401 session-expiry redirect in
            // utils/axios.js) don't reset analytics, so on the next login the
            // previous user is still identified. Calling identify() straight to a
            // new user would MERGE the two identities in both PostHog and Segment.
            // So reset first — but ONLY when we're currently identified as a
            // DIFFERENT user. When the current distinct_id is anonymous we must
            // NOT reset: identify() has to merge the anonymous history (including
            // the cross-domain marketing first-touch) into this user. Resetting
            // via clearSessions keeps PostHog + Segment in sync.
            const identifiedAsOtherUser = posthog?._isIdentified?.() && posthog.get_distinct_id() !== userId
            if (identifiedAsOtherUser) {
                AnalyticsService.clearSessions()
            }

            if (analytics) {
                analytics.identify(userId, data)
            }
            posthog.identify(userId, data) // we need this for posthog to work on error capturing and screen recording so it will assign it to the relevant user, the rest of the evetn will be tracked with Segment

            if (customerIOAnalytics) {
                customerIOAnalytics.identify(userId, data)
            }

        } catch (e) {
            console.log(e)
            handleError(e)
        }
    },
    track(eventName, data = {}) {
        try {
            if (!analytics) return
            // Link the event to the account group so persons connect to groups in
            // PostHog. Segment doesn't carry group state, so $groups must ride on
            // each event (type segment_group, matching AccountGuard's group() call).
            if (currentGroupId && data.$groups === undefined) {
                data = { ...data, $groups: { segment_group: String(currentGroupId) } }
            }
            analytics.track(eventName, data)
        } catch (e) {
            console.log(e)
            handleError(e)
        }

    },
    group(groupId, data = {}) {
        try {
            currentGroupId = groupId
            // posthog.group is sticky — it auto-attaches the group to posthog-js's own
            // captures (session recordings, autocapture), which never go through Segment.
            if (posthog && groupId) {
                posthog.group('segment_group', String(groupId), data)
            }
            if (!analytics) return
            analytics.group(groupId, data)
        } catch (e) {
            console.log(e)
            handleError(e)
        }
    },
    page(pageName, data = {}) {
        try {
            if (!analytics) return
            analytics.page(pageName, data)
        } catch (e) {
            console.log(e)
            handleError(e)
        }

    },
    clearSessions() {
        try {
            currentGroupId = null
            if (analytics) {
                const res = analytics.reset()
                console.log('analytics reset', res)
            }
            if (posthog) {
                const res = posthog.reset()
                console.log('posthog reset', res)
            }
            if (customerIOAnalytics) {
                const res = customerIOAnalytics.reset()
                console.log('customerIOAnalytics reset', res)
            }
        } catch (e) {
            console.log(e)
            handleError(e)
        }
    }
}
